'use client';

import { useMemo } from 'react';

import { useAui, useAuiState } from '@assistant-ui/react';

import { useToolGroup } from '@/components/assistant-ui/tool-group-provider';
import { getUniqueToolCallGroups } from '@/components/assistant-ui/use-tool-group-state';

export function DeferredCarousels() {
  const { ToolGroupComponent } = useToolGroup();
  const api = useAui();
  const isStreaming = useAuiState(({ message }) => message?.status?.type === 'running');

  // Subscribe to a lightweight fingerprint of the message's tool-call structure
  // so the groups recompute when the message content changes (e.g. thread switch
  // where the component is reused at the same position).
  // Similar to contentFingerprint in useToolGroupState but only tracks positions
  // and IDs — not result/error state which don't affect group boundaries.
  const contentFingerprint = useAuiState(({ message }) => {
    const content = message?.content;
    if (!content?.length) return '';
    const parts = content as readonly { type: string; toolCallId?: string }[];
    let fp = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.type === 'tool-call') {
        if (fp) fp += '|';
        fp += `${i}:${part.toolCallId}`;
      }
    }
    return fp;
  });

  const groups = useMemo(() => {
    if (isStreaming) return [];
    const content = api.message.getState().content;
    return content ? getUniqueToolCallGroups(content) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- contentFingerprint triggers recomputation when tool-call structure changes
  }, [isStreaming, api, contentFingerprint]);

  if (groups.length === 0) return null;

  return (
    <>
      {groups.map(({ startIndex, endIndex }) => (
        <ToolGroupComponent
          key={startIndex}
          startIndex={startIndex}
          endIndex={endIndex}
          renderMode="carousel"
        />
      ))}
    </>
  );
}
